import React, { useMemo } from 'react';
import { Form, Table } from 'react-bootstrap';

import {
  CreateUpdatePriceDto,
  GetPriceTypeDto,
  CreateUpdatePriceDtoConsultationTypeEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import numberFormatter from '../../../common/numberFormatter';

export enum PriceFilterType {
  ConsultationType = 'consultationType',
  PricingGroup = 'pricingGroup',
  SupportingExamination = 'supportingExamination',
  Article = 'article',
}

interface PricingServicesTabProps {
  combinations: GetPriceTypeDto[];
  pricelistItems: CreateUpdatePriceDto[];
  onUpdatePrice: (
    index: number,
    price: string,
    priceType?: GetPriceTypeDto,
  ) => void;
  priceFilterType?: PriceFilterType;
}

export const PricingServicesTab: React.FC<PricingServicesTabProps> = ({
  combinations,
  pricelistItems,
  onUpdatePrice,
  priceFilterType,
}) => {
  const getOrCreateItemIndex = (service: GetPriceTypeDto): number => {
    const existingIndex = pricelistItems.findIndex(
      item =>
        item.consultationType ===
          (service.consultationType as unknown as CreateUpdatePriceDtoConsultationTypeEnum) &&
        item.articleId === service.articleId &&
        item.supportingExaminationDictId ===
          service.supportingExaminationDictId &&
        item.pricingGroupId === service.pricingGroupId,
    );
    return existingIndex !== -1 ? existingIndex : pricelistItems.length;
  };

  const filteredCombinations = useMemo((): GetPriceTypeDto[] => {
    if (!priceFilterType) {
      return combinations;
    }

    return combinations.filter(combo => {
      switch (priceFilterType) {
        case PriceFilterType.ConsultationType:
          return combo.consultationType || '';
        case PriceFilterType.PricingGroup:
          return combo.pricingGroupId || '';
        case PriceFilterType.SupportingExamination:
          return combo.supportingExaminationDictId || '';
        case PriceFilterType.Article:
          return combo.articleId || '';
        default:
          return '';
      }
    });
  }, [combinations, priceFilterType]);

  return (
    <Table striped bordered hover responsive size="sm">
      <thead>
        <tr>
          <th>Nazwa</th>
          <th>Cena</th>
        </tr>
      </thead>
      <tbody>
        {filteredCombinations.map(priceType => {
          const itemIndex = getOrCreateItemIndex(priceType);

          return (
            <tr
              key={`${priceType.consultationType}-${priceType.articleId}-${priceType.supportingExaminationDictId}-${priceType.pricingGroupId}`}
            >
              <td>{priceType.name}</td>
              <td>
                <Form.Control
                  size="sm"
                  type="text"
                  value={formatPrice(pricelistItems[itemIndex]?.price) || ''}
                  onChange={e => {
                    onUpdatePrice(itemIndex, e.target.value, priceType);
                  }}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

function formatPrice(
  price: string | null | undefined,
): string | number | string[] | undefined {
  if (price === null || price === undefined) {
    return '';
  }
  const formattedPrice = numberFormatter(price);
  return formattedPrice === 'NaN' ? price : formattedPrice;
}
